<template>
  <app-page
    :title="employee ? $t('employee.optIn.title', { name: employee.full_name }) : $t('employee.optIn.titleFallback')"
    :error="error"
    :previous="[
      { label: $t('employee.previousLabel'), to: { name: 'employees' } },
      {
        label: employee ? employee.full_name : $t('employee.title'),
        to: { name: 'employee', params: { employeeId: $route.params.employeeId } },
      },
    ]"
    icon="user-plus"
  >
    <app-loading :loading="loading" />
    <template v-if="!loading && !saved">
      <sme-alert level="warning" class="mb-3">
        {{ $t('employee.optIn.warningAlert', { name: employee.full_name }) }}
      </sme-alert>

      <ValidationObserver v-slot="v">
        <b-form @submit.prevent="v.handleSubmit(onSubmit)">
          <app-input
            v-model="reason"
            type="text"
            name="reason"
            rules="required"
            :label="$t('employee.offCycle.reasonColumn')"
            :disabled="saving || saved"
            :validate-immediate="false"
          />
          <page-sub-footer>
            <b-button type="submit" variant="primary" :disabled="v.invalid || saving || saved">
              <b-spinner v-if="saving" class="mr-2" small />
              {{ saving ? $t('employee.optIn.submittingButton') : $t('employee.optIn.submitButton') }}
            </b-button>
          </page-sub-footer>
        </b-form>
      </ValidationObserver>
    </template>
    <sme-alert v-if="saved" level="good">
      <p>
        <strong>{{ $t('employee.optIn.successTitle', { name: employee.full_name }) }}</strong>
      </p>
      <template v-if="!integrationsState.state.integrations">
        <p>
          {{ $t('employee.optIn.updatePayrollBody') }}
        </p>
        <template v-if="showBankingUpdate">
          <p>
            <router-link :to="{ name: 'employee-setup', params: { employeeId: employee.employee_id } }">{{
              $t('employee.optIn.updateBankingLink')
            }}</router-link>
          </p>
        </template>
      </template>
      <template v-if="reactivated">
        <p>
          {{ $t('employee.optIn.reactivatedBody') }}
        </p>
      </template>
      <template v-else>
        <p>{{ $t('employee.optIn.pendingReenrolmentBody') }}</p>
      </template>
    </sme-alert>
  </app-page>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import ApiClient from '@/ApiClient';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppPage from '@/components/AppPage.vue';
import PageSubFooter from '@/components/PageSubFooter.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import IntegrationsState from '@/state/IntegrationsState.js';
import State from '@/state/State.js';
import i18n from '@/i18n';

export default {
  name: 'EmployeeOptIn',
  components: { ValidationObserver, AppInput, AppLoading, AppPage, PageSubFooter, SmeAlert },
  data() {
    return {
      loading: true,
      saving: false,
      saved: false,
      error: undefined,
      employee: undefined,
      reason: undefined,
      reactivated: false,
      state: State.state,
      integrationsState: IntegrationsState,
      showBankingUpdate: undefined,
    };
  },
  methods: {
    async onSubmit() {
      this.saving = true;

      if (!this.employee.properties?.optout_completed_at) {
        const payload = { delete_previous_banking: false };
        try {
          await ApiClient.reactivateEmployee(this.employee.employee_id, payload);
          this.reactivated = true;
          this.saved = true;
        } catch (error) {
          this.error = error.message;
        }
      } else {
        try {
          const data = await ApiClient.employeeOptIn(this.employee.employee_id, this.reason);

          if (data.ok) {
            this.saved = true;
          } else {
            this.error = i18n.t('employee.optIn.optInError', { name: this.employee.full_name });
          }
        } catch (error) {
          this.error = error.message;
        }
      }

      this.saving = false;
      this.loading = false;
    },
    checkBanking() {
      // Refresh TODOs
      ApiClient.getUser();
      this.showBankingUpdate = this.state.todos.some(
        todo => todo.action === 'employee.setup' && todo.employee_id === this.employee.employee_id,
      );
    },
    loadData() {
      this.loading = true;
      ApiClient.getEmployee(this.$route.params.employeeId).then(
        employee => {
          this.employee = employee;
          this.loading = false;
        },
        error => {
          this.error = error.message;
          this.loading = false;
        },
      );
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>
